import React, { useCallback, useMemo, useState } from "react";

import { ScheduleModel, dayNamesShort } from "../MonthCalendar";
import { FaTrashAlt } from "react-icons/fa";
import { HiOutlinePencil } from "react-icons/hi";
import { IoChevronForward } from "react-icons/io5";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { formatTime, minutesToTime, useToaster } from "utils";
import { PaziaDeleteModal } from "..";
import { Link } from "react-router-dom";
import { routes } from "consts";
import { COURSE_PLACE_TYPE } from "consts/courseScheduleType";
import { FaLaptop } from "react-icons/fa6";
import { useTopicGroupDetailsQuery } from "api/hooks";
import { ScheduleItemBadge, ScheduleItemPreviewPopover } from "./ScheduleItemPreviewPopover";
import type { ScheduleConflictApi } from "api/courseSchedulesApi";

interface CourseScheduleProps {
  schedules: ScheduleModel[];
  deleteEvent: (courseScheduleId: number) => Promise<void>;
  editEvent?: (courseSchedule: ScheduleModel) => Promise<void>;
  onSelectionChange?: (selectedRows: any[]) => void;
  /** Trainer/room double bookings per entry id; shown as a "Kolizja" chip with the details on hover. */
  conflicts?: Map<number, ScheduleConflictApi[]>;
  describeConflict?: (c: ScheduleConflictApi) => string;
}

const ConflictChip = ({
  items,
  describe,
}: {
  items?: ScheduleConflictApi[];
  describe?: (c: ScheduleConflictApi) => string;
}) =>
  items && items.length > 0 ? (
    <span
      data-testid="schedule-conflict-chip"
      title={items.map((c) => (describe ? describe(c) : c.resource)).join("\n")}
      className="ml-2 inline-flex items-center whitespace-nowrap rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-500/20 dark:text-rose-200"
    >
      Kolizja
    </span>
  ) : null;

interface PreviewState {
  item: ScheduleModel;
  anchorRect: DOMRect;
}

const INITIAL_SORTING = [{ id: "dateFrom", desc: false }];

export const CourseScheduleList = ({
  schedules,
  deleteEvent,
  editEvent,
  onSelectionChange,
  conflicts,
  describeConflict,
}: CourseScheduleProps): JSX.Element => {
  const { showInfo, showError } = useToaster();
  // Local delete state on purpose: the global DeleteResourceContext is shared with other
  // AdminResourceDeleteModals on the schedule tab (trainer cost entries), so both modals used to open
  // and the cost one tried to delete a cost entry by the schedule id.
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const toggleExpanded = useCallback((rowId: number) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  const columns = React.useMemo<TanTableColumn<ScheduleModel>[]>(
    () => [
      {
        id: "expander",
        header: "",
        size: 28,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          if (!row.topicGroupId) return null;
          const isExpanded = expandedRowIds.has(row.id);
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(row.id);
              }}
              className={`flex items-center justify-center p-1 ${
                isExpanded ? "text-primary-700 dark:text-primary-300" : "text-slate-400 hover:text-slate-600"
              }`}
              title={isExpanded ? "Zwiń tematy" : "Pokaż tematy w grupie"}
              aria-expanded={isExpanded}
            >
              <IoChevronForward size={14} className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
            </button>
          );
        },
      },
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        size: 50,
      },
      {
        id: "session.name",
        accessorKey: "session.name",
        header: "Zjazd",
        cell: (info) => <span>{info.row.original.session.name}</span>,
        size: 100,
      },
      {
        id: "dateFrom",
        accessorKey: "dateFrom",
        header: "Dzień",
        enableColumnFilter: false,
        size: 180,
        sortingFn: "datetime",
        // Default sort is ascending - without this the first header click on a Date column would
        // flip to "desc first" semantics and clear the sort instead of reversing it.
        sortDescFirst: false,
        cell: (info) => (
          <div className="flex gap-2">
            <div className="truncate font-semibold">
              {formatTime(info.row.original.dateFrom) + " - " + formatTime(info.row.original.dateTo)}
            </div>
            <div className="truncate">
              {getListDate(info.row.original.dateFrom)}
              {" ("}
              {getShortDayName(info.row.original.dateFrom)}
              {") "}
            </div>
          </div>
        ),
      },
      {
        id: "nameType",
        accessorKey: "nameType",
        header: "Rodzaj",
        size: 40,
        cell: (info) => <ScheduleItemBadge item={info.row.original} />,
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Nazwa",
        // @ts-ignore
        size: "auto",
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          const name = info.getValue<string>();
          const chip = <ConflictChip items={conflicts?.get(row.id)} describe={describeConflict} />;
          if (!row.topicId && !row.topicGroupId)
            return (
              <span>
                {name}
                {chip}
              </span>
            );
          return (
            <span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreview({ item: row, anchorRect: e.currentTarget.getBoundingClientRect() });
                }}
                className="text-left font-medium text-primary-500 hover:text-primary-700 hover:underline dark:text-primary-300 dark:hover:text-primary-200"
                title={row.topicGroupId ? "Podgląd grupy tematów" : "Podgląd tematu"}
              >
                {name}
              </button>
              {chip}
            </span>
          );
        },
      },
      {
        id: "placeName",
        accessorKey: "placeName",
        header: "Miejsce",
        size: 300,
        cell: (info) =>
          info.row.original.placeType === COURSE_PLACE_TYPE.ONLINE ? (
            <div className="flex items-center gap-1">
              <FaLaptop size={14} />
              <span>Online</span>
            </div>
          ) : (
            <span>{info.row.original.placeName}</span>
          ),
      },
      {
        id: "trainersPrices",
        accessorKey: "trainersPrices",
        header: "Trenerzy",
        cell: (info) => (
          <div className="flex flex-col">
            {info.row.original.trainersPrices.map((trainerPrice) => (
              <Link
                key={trainerPrice.trainerId}
                to={routes.TRAINER(trainerPrice.trainerId)}
                target="_blank"
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {trainerPrice.trainerFirstName} {trainerPrice.trainerSurname}
              </Link>
            ))}
          </div>
        ),
      },
      // Actions
      {
        id: "actions",
        accessorKey: "actions",
        header: "Akcje",
        size: 20,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => (
          <div className="flex justify-center gap-2">
            <HiOutlinePencil
              size={18}
              className="cursor-pointer text-slate-500 hover:text-blue-500"
              onClick={(e) => {
                e.stopPropagation();
                if (editEvent) editEvent(info.row.original);
              }}
              title="Edytuj"
            />
            <FaTrashAlt
              size={18}
              className="cursor-pointer text-slate-500 hover:text-red-500"
              title="Usuń"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(info.row.original.id);
              }}
            />
          </div>
        ),
      },
    ],
    [expandedRowIds, toggleExpanded, editEvent, conflicts, describeConflict],
  );

  // Memoized so local UI state changes (expand, preview, delete modal) don't hand TanStack a new data
  // reference - that would reset the table back to page 1.
  const data = useMemo(
    () =>
      schedules.map((schedule) => ({
        ...schedule,
        name: schedule.topicGroupName || schedule.topicName || schedule.examName || "Przerwa",
        nameType: schedule.topicGroupName
          ? "Grupa"
          : schedule.topicName
            ? "Temat"
            : schedule.examName
              ? "Egzamin"
              : "Przerwa",
      })),
    [schedules],
  );

  const groupRowIds = useMemo(() => data.filter((row) => row.topicGroupId).map((row) => row.id), [data]);
  const allGroupsExpanded = groupRowIds.length > 0 && groupRowIds.every((id) => expandedRowIds.has(id));

  const toggleAllGroups = () => setExpandedRowIds(allGroupsExpanded ? new Set() : new Set(groupRowIds));

  // Clicking anywhere on a group row toggles its topic list (interactive cells stop propagation).
  const onRowClick = useCallback((row: ScheduleModel) => toggleExpanded(row.id), [toggleExpanded]);
  const isRowClickable = useCallback((row: ScheduleModel) => !!row.topicGroupId, []);

  const renderSubRows = useCallback(
    (row: ScheduleModel, colSpan: number) =>
      row.topicGroupId && expandedRowIds.has(row.id) ? (
        <GroupTopicsSubRows topicGroupId={row.topicGroupId} colSpan={colSpan} />
      ) : null,
    [expandedRowIds],
  );

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteEvent(deleteId);
      showInfo("Pomyślnie usunięto.");
      setDeleteId(null);
    } catch (e) {
      showError("Wystąpił błąd podczas usuwania.");
      console.error(e);
    }
  };

  return (
    <main className="grow">
      <div>
        {groupRowIds.length > 0 && (
          <div className="mb-2 flex">
            <button
              type="button"
              onClick={toggleAllGroups}
              className="flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
            >
              <IoChevronForward
                size={14}
                className={`transition-transform ${allGroupsExpanded ? "-rotate-90" : "rotate-90"}`}
              />
              {allGroupsExpanded ? "Zwiń wszystkie" : "Rozwiń wszystkie"}
            </button>
          </div>
        )}
        <TanTableClient
          data={data}
          columns={columns}
          hideBorder
          pageSize={20}
          onSelectionChange={onSelectionChange}
          initialSorting={INITIAL_SORTING}
          renderSubRows={renderSubRows}
          onRowClick={onRowClick}
          isRowClickable={isRowClickable}
        />

        <PaziaDeleteModal
          id="delete-schedule-event-modal"
          title="Usuń wydarzenie"
          description="Jesteś pewien że chcesz usunąć to wydarzenie?"
          isOpen={deleteId !== null}
          onCloseRequest={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />

        {preview && (
          <ScheduleItemPreviewPopover
            item={preview.item}
            anchorRect={preview.anchorRect}
            onClose={closePreview}
            onOpenEdit={editEvent}
          />
        )}
      </div>
    </main>
  );
};

const GroupTopicsSubRows = ({ topicGroupId, colSpan }: { topicGroupId: number; colSpan: number }) => {
  const { data: group, isLoading, isError } = useTopicGroupDetailsQuery(topicGroupId);
  const rowClassName = "bg-slate-50 text-[13px] text-slate-600 dark:bg-slate-900/40 dark:text-slate-300";
  const cellClassName = "py-1.5 pl-14 pr-5";

  if (isLoading || isError || !group || group.topics.length === 0) {
    const message = isLoading
      ? "Ładowanie tematów..."
      : isError || !group
        ? "Nie udało się pobrać tematów grupy."
        : "Grupa nie zawiera tematów.";
    return (
      <tr className={rowClassName}>
        <td colSpan={colSpan} className={`${cellClassName} italic text-slate-400`}>
          {message}
        </td>
      </tr>
    );
  }

  // One cell with a compact list - a <tr> per topic inherits the table's row height and spreads the list out.
  return (
    <tr className={rowClassName}>
      <td colSpan={colSpan} className={cellClassName}>
        <ul className="leading-5">
          {group.topics.map((topic) => (
            <li key={topic.id}>
              &bull;&nbsp;&nbsp;{topic.title}
              {topic.time > 0 && <span className="ml-2 text-slate-400">{minutesToTime(topic.time)}</span>}
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
};

function getListDate(date: Date) {
  let day = date.getDate();
  let month = date.getMonth() + 1; // getMonth() returns 0-11
  let year = date.getFullYear().toString().substr(-2); // get last two digits of year

  return day.toString().padStart(2, "0") + "." + month.toString().padStart(2, "0") + "." + year;
}
function getShortDayName(date: Date) {
  // Get the day of the week (0-6) from the Date object
  const dayIndex = date.getDay();

  // Adjust the index to match your array (0 = Sunday, 1 = Monday, etc.)
  // Since your array starts with Monday, you need to map 0 (Sunday) to 6 and shift the rest
  const adjustedIndex = (dayIndex + 6) % 7;

  // Return the corresponding short day name
  return dayNamesShort[adjustedIndex + 1];
}
